import type { ReactNode } from 'react'
import { siteName, siteOrgWord } from '../lib/public-router'
import '@app/shared/error-page.css'

/**
 * The one designed surface for a page that cannot be shown — not found, and a
 * crash.
 *
 * Every state the app can end up in should look like it was drawn on purpose,
 * and these are the two that historically aren't: an unknown address, and an
 * uncaught render error, which leaves a blank white document. A blank page is
 * indistinguishable from a network failure or a dead tab, so the one moment a
 * person most needs to be told what happened is the moment we say nothing.
 *
 * Minimal, in the same vocabulary as everything else — a figure, a sentence,
 * and a way out. It is deliberately NOT a `Notice tone="error"`: a notice sits
 * inside a working page and reports that an action failed. This IS the page.
 */
export function ErrorPage({
  code,
  title,
  children,
  actions,
  heading: Heading = 'h1',
}: {
  /** The condition, as a figure. Muted — the sentence is what gets read. */
  code: string
  title: string
  children: ReactNode
  actions?: ReactNode
  /** h2 where the page sits under the app's top bar, which holds the h1. */
  heading?: 'h1' | 'h2'
}) {
  return (
    <div className="errp">
      <p className="errp__code">{code}</p>
      <Heading className="errp__title">{title}</Heading>
      <p className="errp__body">{children}</p>
      <div className="errp__actions">
        {actions}
        <a className="errp__link" href="/">
          Go to {siteName()}
        </a>
      </div>
    </div>
  )
}

/**
 * An address in the signed-in app that does not exist here — mistyped, or a
 * section another product has and this one does not.
 */
export function AppNotFound() {
  return (
    <ErrorPage code="404" title="That page isn’t here." heading="h2">
      The address may be mistyped, or what it names is not part of {siteName()}.
    </ErrorPage>
  )
}

/**
 * A public address that names a congregation we cannot find — a mistyped
 * identifier, or an org that no longer publishes a page.
 *
 * Used by every public page, so two visitors hitting an unresolvable org see
 * the same thing rather than whichever card that page happened to invent.
 */
export function PublicNotFound({
  subject = siteOrgWord(),
}: {
  /** What was being looked for, so the sentence names it. */
  subject?: string
}) {
  return (
    <ErrorPage code="404" title={`We couldn’t find that ${subject}.`}>
      The address may be mistyped, or this {subject} may no longer publish a
      page here. Check the link you followed.
    </ErrorPage>
  )
}
